import { useEffect, useRef, useState } from "react"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"
import { FloorPlanMode, useDataContext } from "../../hooks/context"
import { MapKeys } from "./utils"
import {
  angleFromCentroid,
  computeCentroid,
  computeRotationHandlePosition,
  rotateCorners,
  scaleFromAnchor,
  skewCorner,
  translateCorners,
} from "./floorPlanCoordinates"

const buildHandleSourceData = corners => {
  const rotationPosition = computeRotationHandlePosition(corners)

  return {
    type: "FeatureCollection",
    features: [
      ...corners.map((coordinates, index) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates },
        properties: { handleType: "corner", index },
      })),
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: rotationPosition },
        properties: { handleType: "rotation" },
      },
    ],
  }
}

const buildBoundsSourceData = corners => ({
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [[...corners, corners[0]]],
      },
      properties: { handleType: "bounds" },
    },
  ],
})

const buildConnectorSourceData = corners => {
  const topEdgeMidpoint = [
    (corners[0][0] + corners[1][0]) / 2,
    (corners[0][1] + corners[1][1]) / 2,
  ]
  const rotationPosition = computeRotationHandlePosition(corners)

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [topEdgeMidpoint, rotationPosition],
        },
        properties: {},
      },
    ],
  }
}

const isValidCorners = corners => Array.isArray(corners) && corners.length === 4

const getDragCursor = dragType => {
  if (dragType === "translate") return "move"
  if (dragType === "rotate") return "crosshair"
  return "nwse-resize"
}

const removeHandlesFromMap = map => {
  if (!map) return

  if (map.getLayer(MapKeys.FP_ROTATION_LAYER)) map.removeLayer(MapKeys.FP_ROTATION_LAYER)
  if (map.getLayer(MapKeys.FP_HANDLE_LAYER)) map.removeLayer(MapKeys.FP_HANDLE_LAYER)
  if (map.getLayer(MapKeys.FP_CONNECTOR_LAYER)) map.removeLayer(MapKeys.FP_CONNECTOR_LAYER)
  if (map.getLayer(MapKeys.FP_BOUNDS_LAYER)) map.removeLayer(MapKeys.FP_BOUNDS_LAYER)

  if (map.getSource(MapKeys.FP_HANDLE_SOURCE)) map.removeSource(MapKeys.FP_HANDLE_SOURCE)
  if (map.getSource(MapKeys.FP_CONNECTOR_SOURCE)) map.removeSource(MapKeys.FP_CONNECTOR_SOURCE)
  if (map.getSource(MapKeys.FP_BOUNDS_SOURCE)) map.removeSource(MapKeys.FP_BOUNDS_SOURCE)
}

/**
 * Renders and manages interactive floor plan positioning handles.
 *
 * @param {Object} props
 * @param {Object} props.map - Mapbox GL map instance
 * @param {boolean} props.isMapLoading - Whether map is still loading
 * @param {boolean} props.showFloorPlan - Whether floor plan layer is visible
 */
export function FloorPlanPositionHandles(props) {
  const { floorPlan, floorPlanActions } = useDataContext()
  const { isFeatureEnabled } = useAnalyticsContext()

  if (!isFeatureEnabled(FeatureFlags.floor_plans)) return null
  if (floorPlan.mode !== FloorPlanMode.POSITIONING) return null
  if (!props.showFloorPlan) return null

  return <FloorPlanPositionHandlesInner {...props} floorPlan={floorPlan} floorPlanActions={floorPlanActions} />
}

function FloorPlanPositionHandlesInner({ map, isMapLoading, floorPlan, floorPlanActions }) {
  const { coordinates } = floorPlan

  const coordinatesRef = useRef(coordinates)
  const updateCoordinatesRef = useRef(floorPlanActions.updateCoordinates)
  const dragStateRef = useRef({
    isDragging: false,
    dragType: null,
    dragIndex: null,
    startLngLat: null,
    startCorners: null,
    startAngle: null,
  })

  coordinatesRef.current = coordinates
  updateCoordinatesRef.current = floorPlanActions.updateCoordinates

  const [isStyleReady, setIsStyleReady] = useState(() => map?.isStyleLoaded?.() ?? false)

  useEffect(() => {
    if (!map) return

    if (map.isStyleLoaded()) {
      setIsStyleReady(true)
      return
    }

    const handleStyleLoad = () => setIsStyleReady(true)
    map.once("style.load", handleStyleLoad)

    return () => {
      map.off("style.load", handleStyleLoad)
    }
  }, [map])

  useEffect(() => {
    if (!map || isMapLoading || !isStyleReady) return

    if (!isValidCorners(coordinates)) {
      removeHandlesFromMap(map)
      return
    }

    const handleSourceData = buildHandleSourceData(coordinates)
    const boundsSourceData = buildBoundsSourceData(coordinates)
    const connectorSourceData = buildConnectorSourceData(coordinates)

    if (!map.getSource(MapKeys.FP_HANDLE_SOURCE)) {
      map.addSource(MapKeys.FP_HANDLE_SOURCE, {
        type: "geojson",
        data: handleSourceData,
      })
    } else {
      map.getSource(MapKeys.FP_HANDLE_SOURCE).setData(handleSourceData)
    }

    if (!map.getSource(MapKeys.FP_BOUNDS_SOURCE)) {
      map.addSource(MapKeys.FP_BOUNDS_SOURCE, {
        type: "geojson",
        data: boundsSourceData,
      })
    } else {
      map.getSource(MapKeys.FP_BOUNDS_SOURCE).setData(boundsSourceData)
    }

    if (!map.getSource(MapKeys.FP_CONNECTOR_SOURCE)) {
      map.addSource(MapKeys.FP_CONNECTOR_SOURCE, {
        type: "geojson",
        data: connectorSourceData,
      })
    } else {
      map.getSource(MapKeys.FP_CONNECTOR_SOURCE).setData(connectorSourceData)
    }

    if (!map.getLayer(MapKeys.FP_BOUNDS_LAYER)) {
      map.addLayer({
        id: MapKeys.FP_BOUNDS_LAYER,
        type: "fill",
        source: MapKeys.FP_BOUNDS_SOURCE,
        paint: {
          "fill-color": "#4A90D9",
          "fill-opacity": 0.05,
        },
      })
    }

    if (!map.getLayer(MapKeys.FP_CONNECTOR_LAYER)) {
      map.addLayer({
        id: MapKeys.FP_CONNECTOR_LAYER,
        type: "line",
        source: MapKeys.FP_CONNECTOR_SOURCE,
        paint: {
          "line-color": "#4CAF50",
          "line-width": 1,
          "line-dasharray": [3, 3],
        },
      })
    }

    if (!map.getLayer(MapKeys.FP_HANDLE_LAYER)) {
      map.addLayer({
        id: MapKeys.FP_HANDLE_LAYER,
        type: "circle",
        source: MapKeys.FP_HANDLE_SOURCE,
        filter: ["==", ["get", "handleType"], "corner"],
        paint: {
          "circle-radius": 6,
          "circle-color": "#4A90D9",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFFFFF",
        },
      })
    }

    if (!map.getLayer(MapKeys.FP_ROTATION_LAYER)) {
      map.addLayer({
        id: MapKeys.FP_ROTATION_LAYER,
        type: "circle",
        source: MapKeys.FP_HANDLE_SOURCE,
        filter: ["==", ["get", "handleType"], "rotation"],
        paint: {
          "circle-radius": 7,
          "circle-color": "#4CAF50",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFFFFF",
        },
      })
    }
  }, [map, isMapLoading, isStyleReady, coordinates])

  useEffect(() => {
    if (!map || isMapLoading || !isStyleReady) return

    const handleMouseEnterHandles = event => {
      if (dragStateRef.current.isDragging) return
      const handleType = event.features?.[0]?.properties?.handleType
      map.getCanvas().style.cursor = handleType === "rotation" ? "crosshair" : "nwse-resize"
    }

    const handleMouseLeaveHandles = () => {
      if (dragStateRef.current.isDragging) return
      map.getCanvas().style.cursor = ""
    }

    const handleMouseEnterBounds = () => {
      if (dragStateRef.current.isDragging) return
      map.getCanvas().style.cursor = "move"
    }

    const handleMouseLeaveBounds = () => {
      if (dragStateRef.current.isDragging) return
      map.getCanvas().style.cursor = ""
    }

    const startDrag = (dragType, dragIndex, event) => {
      const currentCorners = coordinatesRef.current
      if (!isValidCorners(currentCorners)) return

      const startPoint = [event.lngLat.lng, event.lngLat.lat]
      const nextDragState = {
        isDragging: true,
        dragType,
        dragIndex,
        startLngLat: startPoint,
        startCorners: currentCorners.map(corner => [...corner]),
        startAngle: null,
      }

      if (dragType === "rotate") {
        const centroid = computeCentroid(nextDragState.startCorners)
        nextDragState.startAngle = angleFromCentroid(centroid, startPoint)
      }

      dragStateRef.current = nextDragState
      map.getCanvas().style.cursor = getDragCursor(dragType)
      map.dragPan.disable()
      map.boxZoom.disable()
      window.addEventListener("mouseup", handleMouseUp, { once: true })
    }

    const handleMouseDownHandles = event => {
      const feature = event.features?.[0]
      const handleType = feature?.properties?.handleType

      if (handleType === "corner") {
        const rawIndex = Number(feature?.properties?.index)
        if (Number.isNaN(rawIndex)) return
        startDrag(event.originalEvent?.shiftKey ? "skew" : "scale", rawIndex, event)
        return
      }

      if (handleType === "rotation") {
        startDrag("rotate", null, event)
      }
    }

    const handleMouseDownBounds = event => {
      if (dragStateRef.current.isDragging) return
      startDrag("translate", null, event)
    }

    const handleMouseMove = event => {
      if (!dragStateRef.current.isDragging) return

      const dragPoint = [event.lngLat.lng, event.lngLat.lat]
      const { dragType, dragIndex, startLngLat, startCorners, startAngle } = dragStateRef.current

      /** @type {FloorPlanCorners | null} */
      let nextCorners = null

      if (dragType === "translate") {
        nextCorners = translateCorners(
          startCorners,
          dragPoint[0] - startLngLat[0],
          dragPoint[1] - startLngLat[1]
        )
      } else if (dragType === "scale") {
        nextCorners = scaleFromAnchor(startCorners, dragIndex, dragPoint)
      } else if (dragType === "skew") {
        nextCorners = skewCorner(startCorners, dragIndex, dragPoint)
      } else if (dragType === "rotate") {
        const centroid = computeCentroid(startCorners)
        const currentAngle = angleFromCentroid(centroid, dragPoint)
        let delta = currentAngle - startAngle
        while (delta > Math.PI) delta -= 2 * Math.PI
        while (delta < -Math.PI) delta += 2 * Math.PI
        nextCorners = rotateCorners(startCorners, delta)
      }

      if (nextCorners) {
        map.getCanvas().style.cursor = getDragCursor(dragType)
        updateCoordinatesRef.current(nextCorners)
      }
    }

    const handleMouseUp = () => {
      if (!dragStateRef.current.isDragging) return

      dragStateRef.current = {
        isDragging: false,
        dragType: null,
        dragIndex: null,
        startLngLat: null,
        startCorners: null,
        startAngle: null,
      }

      map.getCanvas().style.cursor = ""
      if (floorPlan.mode !== FloorPlanMode.POSITIONING) {
        map.dragPan.enable()
      }
      window.removeEventListener("mouseup", handleMouseUp)
    }

    map.on("mouseenter", MapKeys.FP_HANDLE_LAYER, handleMouseEnterHandles)
    map.on("mouseleave", MapKeys.FP_HANDLE_LAYER, handleMouseLeaveHandles)
    map.on("mouseenter", MapKeys.FP_ROTATION_LAYER, handleMouseEnterHandles)
    map.on("mouseleave", MapKeys.FP_ROTATION_LAYER, handleMouseLeaveHandles)
    map.on("mouseenter", MapKeys.FP_BOUNDS_LAYER, handleMouseEnterBounds)
    map.on("mouseleave", MapKeys.FP_BOUNDS_LAYER, handleMouseLeaveBounds)

    map.on("mousedown", MapKeys.FP_HANDLE_LAYER, handleMouseDownHandles)
    map.on("mousedown", MapKeys.FP_ROTATION_LAYER, handleMouseDownHandles)
    map.on("mousedown", MapKeys.FP_BOUNDS_LAYER, handleMouseDownBounds)
    map.on("mousemove", handleMouseMove)
    map.on("mouseup", handleMouseUp)

    return () => {
      map.getCanvas().style.cursor = ""
      if (floorPlan.mode !== FloorPlanMode.POSITIONING) {
        map.dragPan.enable()
      }
      window.removeEventListener("mouseup", handleMouseUp)

      map.off("mouseenter", MapKeys.FP_HANDLE_LAYER, handleMouseEnterHandles)
      map.off("mouseleave", MapKeys.FP_HANDLE_LAYER, handleMouseLeaveHandles)
      map.off("mouseenter", MapKeys.FP_ROTATION_LAYER, handleMouseEnterHandles)
      map.off("mouseleave", MapKeys.FP_ROTATION_LAYER, handleMouseLeaveHandles)
      map.off("mouseenter", MapKeys.FP_BOUNDS_LAYER, handleMouseEnterBounds)
      map.off("mouseleave", MapKeys.FP_BOUNDS_LAYER, handleMouseLeaveBounds)

      map.off("mousedown", MapKeys.FP_HANDLE_LAYER, handleMouseDownHandles)
      map.off("mousedown", MapKeys.FP_ROTATION_LAYER, handleMouseDownHandles)
      map.off("mousedown", MapKeys.FP_BOUNDS_LAYER, handleMouseDownBounds)
      map.off("mousemove", handleMouseMove)
      map.off("mouseup", handleMouseUp)
    }
  }, [map, isMapLoading, isStyleReady, floorPlan.mode])

  useEffect(() => {
    return () => {
      if (!map) return
      map.dragPan.enable()
      removeHandlesFromMap(map)
    }
  }, [map])

  return null
}
